import { Box, Flex, Grid, GridItem, Image, Text, useBoolean } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { PropsWithChildren, useMemo, useState } from "react";
import { ReactComponent as EmptyAlbum } from "@/assets/icons/mySpace/empty_album.svg";
import { themeStore } from "@/stores/theme";
import Icon from "@/components/Icon";
import { albumStore } from "@/modules/album/store";
import RenameModal from "@/modules/album/components/RenameModal";
import DeleteModal from "@/modules/album/components/DeleteModal";
import OperationMenu from "@/modules/album/components/OperationMenu";
import { albumOpList } from "@/modules/album/constant";
import { langStore, translate } from "@noli/business";
import { AlbumWords } from "@/utils/lang/album";
const cardW = "405px";
const cardH = "380px";

interface CardProps {
  id: number;
  total: number;
  name: string;
  preview: string[];
  isDefault: boolean;
  onClick: () => void;
}

function withCompress(url: string) {
  return url + "?x-oss-process=image/resize,w_350";
}

function AlbumCard(props: PropsWithChildren<CardProps>) {
  const { preview, name, isDefault, total, id } = props;
  const { panelBg, boardBg } = themeStore.styles;
  const lang = langStore.tgt;
  const [p1, p2, p3] = preview;
  const [flag, flagOp] = useBoolean(false);
  const [modalType, setModalType] = useState<"rename" | "delete">("rename");

  const onViewAlbum = () => {
    albumStore.getAlbumDetail(id).then(props.onClick);
  };

  const onMenuSelect = (type: string) => {
    setModalType(type as "rename" | "delete");
    flagOp.on();
  };

  const modal = useMemo(() => {
    switch (modalType) {
      case "delete":
        return <DeleteModal isOpen={flag} onClose={flagOp.off} albumId={id} />;
      case "rename":
        return <RenameModal isOpen={flag} onClose={flagOp.off} albumId={id} />;
    }
  }, [flag, id, flagOp]);

  return (
    <Grid
      w={cardW}
      h={cardH}
      flexShrink={0}
      gridTemplateColumns="repeat(3, 1fr)"
      gridTemplateRows="repeat(3, 1fr)"
      gap="2px"
      rounded="20px"
      overflow="hidden"
      userSelect="none"
      mb="30px"
      mx="15px"
      onClick={onViewAlbum}
      cursor="pointer"
    >
      <GridItem rowSpan={2} colSpan={2} overflow="hidden">
        <Flex
          h="100%"
          w="100%"
          bg={panelBg}
          overflow="hidden"
          borderColor={boardBg}
          justify="center"
          align="center"
        >
          {p1 ? (
            <Image src={withCompress(p1)} minH="100%" minW="100%" objectFit="cover" />
          ) : (
            <Icon squared={false} svg={EmptyAlbum} />
          )}
        </Flex>
      </GridItem>
      <GridItem boxSize="135px" bg={panelBg} borderColor={boardBg} overflow="hidden">
        {p2 ? <Image src={withCompress(p2)} minH="100%" minW="100%" objectFit="cover" /> : null}
      </GridItem>
      <GridItem boxSize="135px" bg={panelBg} overflow="hidden">
        {p3 ? <Image src={withCompress(p3)} minH="100%" minW="100%" objectFit="cover" /> : null}
      </GridItem>

      <GridItem colSpan={3} px="14px" py="8px" bg={panelBg}>
        <Text color="white" fontSize="32px" fontWeight={600}>
          {name}
        </Text>
        <Flex justify="space-between" color="#999999">
          <Text fontSize="24px">
            {total}
            {translate(AlbumWords.pictureUnit, lang)}
          </Text>
          {isDefault ? null : (
            <OperationMenu placement="top-end" onSelect={onMenuSelect} menuList={albumOpList} />
          )}
        </Flex>
      </GridItem>
      {modal}
    </Grid>
  );
}

export default observer(AlbumCard);
